import React, { useState, useEffect, useRef, useCallback } from "react";
import apiClient from "../../api/apiClient";
import "./TransferWeightModal.css";
import { useConfirm } from "../../context/confirmContext";
import { useEscapeKey } from "../ui/useEscapeKey";

const UNITS = ["g", "oz", "lb", "units"];

/**
 * TransferWeightModal
 * Internal weight transfer between root inventory and locations within the same license.
 *
 * Props:
 *   coaId          — COA.id (stable link key used to find/create the product at the target)
 *   sourceLicenseNo — licenseNo of the entity transferring FROM
 *   sourceLocationId — locationId of the source, or null for license-level
 *   onClose        — called when modal should close
 *   onSuccess      — called after a successful transfer (optional) 
 */
const TransferWeightModal = ({ coaId, sourceLicenseNo, sourceLocationId = null, defaultTarget = null, onClose, onSuccess }) => {
    const confirm = useConfirm();
    useEscapeKey(onClose);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [selectedTarget, setSelectedTarget] = useState(defaultTarget);
    const [weight, setWeight] = useState("");
    const [unit, setUnit] = useState("lb");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const debounceRef = useRef(null);
    const searchWrapRef = useRef(null);

    // Debounced search — fires 300 ms after the user stops typing
    useEffect(() => {
        if (!searchTerm.trim()) {
            setSearchResults([]);
            setShowResults(false);
            return;
        }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await apiClient.get(
                    `/transfer/search?q=${encodeURIComponent(searchTerm)}&excludeLicenseNo=${encodeURIComponent(sourceLicenseNo || "")}`
                );
                setSearchResults(res.data?.results || []);
                setShowResults(true);
            } catch {
                setSearchResults([]);
            }
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [searchTerm, sourceLicenseNo]);

    // Close results dropdown on outside click
    const handleOutsideClick = useCallback((e) => {
        if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
            setShowResults(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [handleOutsideClick]);

    const selectTarget = (result) => {
        setSelectedTarget(result);
        setSearchTerm("");
        setSearchResults([]);
        setShowResults(false);
        setError("");
    };

    const clearTarget = () => {
        setSelectedTarget(null);
        setError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedTarget) { setError("Select a destination location."); return; }
        const weightNum = parseFloat(weight);
        if (isNaN(weightNum) || weightNum <= 0) { setError("Enter a valid weight greater than 0."); return; }

        const ok = await confirm({
            title: "Confirm Transfer",
            message: `Transfer inventory to the selected destination?`,
            confirmLabel: "Transfer",
            variant: "warning",
        });
        if (!ok) return;

        setSubmitting(true);
        setError("");
        try {
            await apiClient.post("/transfer/weight", {
                coaId,
                sourceLicenseNo,
                sourceLocationId: sourceLocationId || null,
                targetLicenseNo: selectedTarget.licenseNo,
                targetLocationId: selectedTarget.type === "location" ? selectedTarget.locationId : null,
                weight: weightNum,
                weightUnit: unit,
            });
            setSuccess(true);
            onSuccess?.();
        } catch (err) {
            setError(err.response?.data?.error || "Transfer failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const targetLabel = selectedTarget
        ? selectedTarget.type === "location"
            ? `${selectedTarget.locationName} — ${selectedTarget.company}`
            : `${selectedTarget.company} (${selectedTarget.licenseNo})`
        : null;

    return (
        <div className="tw-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
            <div className="tw-modal" onClick={(e) => e.stopPropagation()}>
                <div className="tw-modal__header">
                    <h3 className="tw-modal__title">Transfer Weight</h3>
                    <button className="tw-modal__close" onClick={onClose}>×</button>
                </div>

                {success ? (
                    <div className="tw-modal__body">
                        <div className="tw-modal__success">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                            <p>Transfer completed successfully.</p>
                            <button className="tw-modal__btn tw-modal__btn--primary" onClick={onClose}>
                                Done
                            </button>
                        </div>
                    </div>
                ) : (
                    <form className="tw-modal__body" onSubmit={handleSubmit}>

                        {/* Destination search */}
                        <div className="tw-modal__field">
                            <label className="tw-modal__label">Transfer To</label>

                            {selectedTarget ? (
                                <div className="tw-modal__target-chip">
                                    <span className="tw-modal__target-type">
                                        {selectedTarget.type === "location" ? "Location" : "License"}
                                    </span>
                                    <span className="tw-modal__target-name">{targetLabel}</span>
                                    <button
                                        type="button"
                                        className="tw-modal__target-clear"
                                        onClick={clearTarget}
                                        aria-label="Clear selection"
                                    >
                                        ×
                                    </button>
                                </div>
                            ) : (
                                <div className="tw-modal__search-wrap" ref={searchWrapRef}>
                                    <input
                                        type="text"
                                        className="tw-modal__input"
                                        placeholder="Search location name…"
                                        value={searchTerm}
                                        onChange={(e) => { setSearchTerm(e.target.value); setError(""); }}
                                        autoComplete="off"
                                    />
                                    {showResults && searchResults.length > 0 && (
                                        <ul className="tw-modal__results">
                                            {searchResults.map((r, i) => (
                                                <li
                                                    key={i}
                                                    className="tw-modal__result-item"
                                                    onMouseDown={() => selectTarget(r)}
                                                >
                                                    <span className="tw-modal__result-type">
                                                        {r.type === "location" ? "Location" : "License"}
                                                    </span>
                                                    <span className="tw-modal__result-name">
                                                        {r.type === "location"
                                                            ? `${r.locationName} — ${r.company}`
                                                            : `${r.company} (${r.licenseNo})`}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {showResults && searchResults.length === 0 && searchTerm.trim() && (
                                        <ul className="tw-modal__results">
                                            <li className="tw-modal__result-empty">No results found</li>
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Weight input */}
                        <div className="tw-modal__field">
                            <label className="tw-modal__label">Weight</label>
                            <div className="tw-modal__qty-row">
                                <input
                                    type="number"
                                    className="tw-modal__input"
                                    value={weight}
                                    onChange={(e) => { setWeight(e.target.value); setError(""); }}
                                    min="0.01"
                                    step="0.01"
                                    placeholder="0.00"
                                    required
                                />
                                <select
                                    className="tw-modal__unit-select"
                                    value={unit}
                                    onChange={(e) => setUnit(e.target.value)}
                                >
                                    {UNITS.map((u) => (
                                        <option key={u} value={u}>{u}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {error && <p className="tw-modal__error">{error}</p>}

                        <div className="tw-modal__footer">
                            <button type="button" className="tw-modal__btn" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="tw-modal__btn tw-modal__btn--primary"
                                disabled={submitting || !selectedTarget}
                            >
                                {submitting ? "Transferring…" : "Transfer"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default TransferWeightModal;
